import { ListOutput } from '@allocab/allocab-commons-shared-types';
import {
  DriverWithStatusVehicleTripCountOutput,
  GetDriversFleetManagerInput,
} from '@allocab/allocab-driver-service-types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { InfiniteQueryOptions } from '../services/datastore/types';
import { driverApi } from '../services/network/Api';

export const DRIVERS_LIMIT = 10;

export type Input = Omit<GetDriversFleetManagerInput, 'offset' | 'limit'> & { limit?: number };

export const fetchDrivers = (input: GetDriversFleetManagerInput) =>
  driverApi.get<GetDriversFleetManagerInput, DriverWithStatusVehicleTripCountOutput, 'list'>(
    '/v1/driver/account',
    input,
  );

export const useFethDriversPage = (input?: Input, pageNumber = 1) => {
  const limit = input?.limit ?? DRIVERS_LIMIT;
  const apiInput: GetDriversFleetManagerInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  return useQuery(queryKeys.user.fetchDrivers(apiInput), () => fetchDrivers(apiInput), {
    keepPreviousData: true,
  });
};

export const prefetchDriversPage = (input?: Input, pageNumber = 1) => {
  const limit = input?.limit ?? DRIVERS_LIMIT;
  const apiInput: GetDriversFleetManagerInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  queryClient.prefetchQuery(queryKeys.user.fetchDrivers(apiInput), () => fetchDrivers(apiInput), {
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useInfiniteFetchDrivers = (
  input: Input,
  options: InfiniteQueryOptions<
    typeof queryKeys.user.fetchInfiniteDrivers,
    ListOutput<DriverWithStatusVehicleTripCountOutput>
  > = {},
) => {
  const limit = input.limit ?? DRIVERS_LIMIT;
  const fetchDriversQuery = useInfiniteQuery(
    queryKeys.user.fetchInfiniteDrivers(input),
    ({ pageParam = 0 }: { pageParam?: number }) =>
      fetchDrivers({ limit, offset: pageParam, orderBy: 'account.status', ...input }),
    {
      getNextPageParam: (lastPage, allPages) => (lastPage.nbItems < limit ? undefined : limit * allPages.length),
      ...options,
    },
  );

  return fetchDriversQuery;
};
